/** This module exists to avoid cyclic import between, for example, ./reducers/Order and ./thunks/Order
 *
 * Thunks are imported and registered here by ./Store
 */

import type { AnyAction } from 'redux';
import type { ThunkAction, ThunkDispatch } from 'redux-thunk';
import type { RootState } from './reducers';

export type IndirectAction<Payload = unknown> = {
  type: string;
  payload?: Payload;
};

export type IndirectDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
export type IndirectGetState = () => RootState;
export type IndirectThunk = ThunkAction<unknown, RootState, unknown, AnyAction>;
export type IndirectThunkCreator<
  Action extends IndirectAction = IndirectAction,
> = (action: Action) => IndirectThunk;

// The registry contains creators for different action shapes. `never` keeps
// their individual parameter types intact while they are stored together.
type RegisteredThunkCreator = (action: never) => IndirectThunk;

export type IndirectThunkEntry = readonly [string, RegisteredThunkCreator];
const registry: Partial<Record<string, RegisteredThunkCreator>> = {};

export function registerCreators(thunks: Iterable<IndirectThunkEntry>): void {
  for (const [actionType, thunk] of thunks) {
    registry[actionType] = thunk;
  }
}

export function createThunk(action: IndirectAction): IndirectThunk {
  const thunk = registry[action.type];
  if (!thunk) {
    throw new Error(
      `No thunk creator registered for action type: ${action.type}`
    );
  }
  // The action type is the runtime discriminator for the registered creator.
  return (thunk as IndirectThunkCreator)(action);
}
